import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { formatINR, formatDate } from "@/lib/format";
import { printSection } from "@/lib/print";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import PrintBill from "@/components/PrintBill";
import SettlementBox from "@/components/SettlementBox";
import SettlementVoucher from "@/components/SettlementVoucher";
import MobileInput from "@/components/MobileInput";
import { isValidMobile } from "@/lib/format";
import {
  ArrowLeft, Printer, Trash2, CheckCircle2, RotateCcw, Loader2, Truck,
} from "lucide-react";

const statusColor = { available: "#16A34A", booked: "#D97706", sold: "#2563EB", delivered: "#475569" };

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isOwner } = useAuth();
  const [v, setV] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState("");

  // forms
  const [adv, setAdv] = useState({ amount: "", mode: "cash", utr: "", customer_name: "", customer_mobile: "" });
  const [refund, setRefund] = useState({ amount: "", narration: "" });
  const [sale, setSale] = useState({ selling_price: "", customer_name: "", customer_mobile: "" });
  const [pay, setPay] = useState({ mode: "cash", amount: "", utr: "" });
  const [settleVoucher, setSettleVoucher] = useState(null);

  const load = useCallback(() => {
    api.get(`/vehicles/${id}`).then((r) => {
      setV(r.data);
      setSale((s) => ({
        selling_price: r.data.selling_price || "",
        customer_name: r.data.customer_name || "",
        customer_mobile: r.data.customer_mobile || "",
      }));
    });
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const call = async (label, fn) => {
    setBusy(label);
    try {
      const { data } = await fn();
      setV(data);
      toast.success("Saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  if (!v) return <div className="text-slate-400 font-semibold">Loading…</div>;
  const t = v.totals || {};

  const doAdvanceIn = () => {
    if (adv.customer_mobile && !isValidMobile(adv.customer_mobile)) {
      return toast.error("Please enter a valid 10-digit mobile number");
    }
    return call("advin", () => api.post(`/vehicles/${id}/advance-in`, { ...adv, amount: parseFloat(adv.amount || 0) })).then(() =>
      setAdv({ amount: "", mode: "cash", utr: "", customer_name: "", customer_mobile: "" })
    );
  };
  const doRefund = () =>
    call("refund", () => api.post(`/vehicles/${id}/advance-out`, { amount: parseFloat(refund.amount || 0), narration: refund.narration }));
  const doSetSale = () => {
    if (sale.customer_mobile && !isValidMobile(sale.customer_mobile)) {
      return toast.error("Please enter a valid 10-digit mobile number");
    }
    return call("sale", () => api.post(`/vehicles/${id}/sell`, { ...sale, selling_price: parseFloat(sale.selling_price || 0) }));
  };
  const doPay = () =>
    call("pay", () => api.post(`/vehicles/${id}/payment`, { ...pay, amount: parseFloat(pay.amount || 0) })).then(() =>
      setPay({ mode: "cash", amount: "", utr: "" })
    );
  const printSettlement = (data) => {
    setSettleVoucher(data);
    printSection("settlement");
  };
  const setStatus = (status) => call("status", () => api.post(`/vehicles/${id}/status`, { status }));

  const del = async () => {
    if (!window.confirm("Delete this car permanently?")) return;
    try {
      await api.delete(`/vehicles/${id}`);
      toast.success("Deleted");
      navigate("/inventory");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const mediatorHold = v.mediator_balance_hold || 0;

  return (
    <div className="a1-fade max-w-5xl">
      <PrintBill vehicle={v} notes={notes} />
      <SettlementVoucher data={settleVoucher} />

      <div className="no-print">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-500 font-bold mb-4">
          <ArrowLeft className="w-5 h-5" /> Back
        </button>

        {/* Header */}
        <div className="a1-card p-6 mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-head font-black text-3xl tracking-tight">{v.model}</h1>
              <span className="text-xs font-bold px-3 py-1.5 rounded-full text-white capitalize" style={{ background: statusColor[v.status] }}>
                {v.status}
              </span>
            </div>
            <div className="num font-bold text-slate-500 text-lg mt-1">{v.reg_no}</div>
            <div className="text-sm font-semibold text-slate-400 mt-1">
              {v.year || "—"} • {v.color || "—"} • Inward {formatDate(v.inward_date)} • via {v.channel}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button data-testid="print-bill-btn" onClick={() => printSection("bill")} className="a1-btn a1-btn-action !h-12 !text-base">
              <Printer className="w-5 h-5" /> Print Bill
            </button>
            {isOwner && (
              <button data-testid="delete-vehicle" onClick={del} className="a1-btn a1-btn-ghost !h-12 !text-base !text-red-600 !border-red-200">
                <Trash2 className="w-5 h-5" /> Delete
              </button>
            )}
          </div>
        </div>

        {/* Purchase & mediator info */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <div className="a1-card p-5">
            <div className="a1-label">{v.channel === "commission" ? "Customer Expected Price" : "Office Purchase Rate"}</div>
            <div className="num font-head font-black text-2xl">{formatINR(v.channel === "commission" ? v.customer_expected_price : v.office_purchase_rate)}</div>
          </div>
          <div className="a1-card p-5">
            <div className="a1-label">Party / Seller</div>
            <div className="font-bold text-lg">{v.party_name || "—"}</div>
            <div className="text-sm font-semibold text-slate-400">{v.party_mobile} {v.party_city && `• ${v.party_city}`}</div>
          </div>
          <div className="a1-card p-5">
            <div className="a1-label">{v.channel === "commission" ? "Agency Commission" : "Mediator Balance on Hold"}</div>
            {v.channel === "commission" ? (
              <div className="num font-head font-black text-2xl" style={{ color: "#16A34A" }}>{formatINR(v.commission_earned)}</div>
            ) : (
              <div className="num font-head font-black text-2xl" style={{ color: mediatorHold > 0 ? "#EA580C" : "#16A34A" }}>{formatINR(mediatorHold)}</div>
            )}
          </div>
        </div>

        {/* Settle mediator */}
        {mediatorHold > 0 && (
          <div className="a1-card p-6 mb-6" data-testid="settle-mediator-card">
            <h3 className="font-head font-extrabold text-xl tracking-tight mb-3">Settle Mediator Balance</h3>
            <p className="text-slate-500 font-semibold text-sm mb-4">Pay part or all of the ₹{mediatorHold.toLocaleString("en-IN")} held balance and print a settlement voucher.</p>
            <SettlementBox vehicle={v} hold={mediatorHold} onSettled={load} onPrint={printSettlement} />
          </div>
        )}

        {/* Advance in / refund */}
        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          <div className="a1-card p-6">
            <h3 className="font-head font-extrabold text-xl tracking-tight mb-4">Collect Token Advance</h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="a1-label">Amount</label>
                <input data-testid="adv-amount" type="number" className="a1-input" value={adv.amount} onChange={(e) => setAdv({ ...adv, amount: e.target.value })} />
              </div>
              <div>
                <label className="a1-label">Mode</label>
                <select data-testid="adv-mode" className="a1-input" value={adv.mode} onChange={(e) => setAdv({ ...adv, mode: e.target.value })}>
                  <option value="cash">Cash</option>
                  <option value="upi">UPI / Online</option>
                </select>
              </div>
              <div>
                <label className="a1-label">Customer Name</label>
                <input data-testid="adv-name" className="a1-input" value={adv.customer_name} onChange={(e) => setAdv({ ...adv, customer_name: e.target.value })} />
              </div>
              <div>
                <label className="a1-label">Mobile</label>
                <MobileInput testid="adv-mobile" value={adv.customer_mobile} onChange={(v) => setAdv({ ...adv, customer_mobile: v })} />
              </div>
              {adv.mode === "upi" && (
                <div className="col-span-2">
                  <label className="a1-label">UTR / Ref No</label>
                  <input data-testid="adv-utr" className="a1-input" value={adv.utr} onChange={(e) => setAdv({ ...adv, utr: e.target.value })} />
                </div>
              )}
            </div>
            {v.status === "booked" && (() => {
              const inAdv = (v.advances || []).filter((a) => a.direction === "in");
              const firstAdv = inAdv[0] || {};
              const totalAdv = v.totals?.advance_in ?? inAdv.reduce((s, a) => s + a.amount, 0);
              const modeTxt = firstAdv.mode === "upi" ? "UPI" : firstAdv.mode === "bank" ? "Bank" : "Cash";
              return (
                <div className="border-dashed border-2 border-orange-300 rounded-lg p-3 bg-orange-50/50 mb-3" data-testid="active-booking-info">
                  <div className="text-xs font-black uppercase tracking-wide text-[#EA580C] mb-2">Active Booking — Token Collected</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                    <div className="flex justify-between sm:block">
                      <span className="text-xs font-bold text-slate-400">Customer Name</span>
                      <span className="font-bold sm:block">{v.customer_name || "—"}</span>
                    </div>
                    <div className="flex justify-between sm:block">
                      <span className="text-xs font-bold text-slate-400">Contact</span>
                      <span className="num font-bold sm:block">{v.customer_mobile || "—"}</span>
                    </div>
                    <div className="flex justify-between sm:block">
                      <span className="text-xs font-bold text-slate-400">Advance Amount Paid</span>
                      <span className="num font-bold text-[#16A34A] sm:block">{formatINR(totalAdv)} <span className="text-xs font-semibold text-slate-500">(Mode: {modeTxt})</span></span>
                    </div>
                    <div className="flex justify-between sm:block">
                      <span className="text-xs font-bold text-slate-400">Booking Date</span>
                      <span className="num font-bold sm:block">{firstAdv.date ? formatDate(firstAdv.date) : "—"}</span>
                    </div>
                  </div>
                </div>
              );
            })()}
            <button data-testid="adv-submit" onClick={doAdvanceIn} disabled={busy === "advin"} className="a1-btn a1-btn-action w-full">
              {busy === "advin" ? <Loader2 className="w-5 h-5 animate-spin" /> : "Collect Advance → Book Car"}
            </button>
          </div>

          <div className="a1-card p-6">
            <h3 className="font-head font-extrabold text-xl tracking-tight mb-4">Refund / Cancel Advance</h3>
            <div className="mb-4">
              <label className="a1-label">Refund Amount</label>
              <input data-testid="refund-amount" type="number" className="a1-input" value={refund.amount} onChange={(e) => setRefund({ ...refund, amount: e.target.value })} />
            </div>
            <div className="mb-4">
              <label className="a1-label">Narration</label>
              <input data-testid="refund-narration" className="a1-input" value={refund.narration} onChange={(e) => setRefund({ ...refund, narration: e.target.value })} placeholder="Booking cancelled" />
            </div>
            <button data-testid="refund-submit" onClick={doRefund} disabled={busy === "refund"} className="a1-btn a1-btn-ghost w-full">
              {busy === "refund" ? <Loader2 className="w-5 h-5 animate-spin" /> : <><RotateCcw className="w-5 h-5" /> Refund Advance</>}
            </button>
          </div>
        </div>

        {/* Final sale */}
        <div className="a1-card p-6 mb-6">
          <h3 className="font-head font-extrabold text-xl tracking-tight mb-4">Final Sale &amp; Multi-Split Payment</h3>
          <div className="grid md:grid-cols-3 gap-4 mb-5">
            <div>
              <label className="a1-label">Final Selling Price</label>
              <input data-testid="sale-price" type="number" className="a1-input" value={sale.selling_price} onChange={(e) => setSale({ ...sale, selling_price: e.target.value })} />
            </div>
            <div>
              <label className="a1-label">Customer Name</label>
              <input data-testid="sale-name" className="a1-input" value={sale.customer_name} onChange={(e) => setSale({ ...sale, customer_name: e.target.value })} />
            </div>
            <div>
              <label className="a1-label">Mobile</label>
              <MobileInput testid="sale-mobile" value={sale.customer_mobile} onChange={(v) => setSale({ ...sale, customer_mobile: v })} />
            </div>
          </div>
          <button data-testid="sale-submit" onClick={doSetSale} disabled={busy === "sale"} className="a1-btn a1-btn-primary mb-6">
            {busy === "sale" ? <Loader2 className="w-5 h-5 animate-spin" /> : "Set Selling Price"}
          </button>

          {/* Add payment */}
          <div className="border-t border-slate-200 pt-5">
            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="a1-label">Payment Mode</label>
                <select data-testid="pay-mode" className="a1-input" value={pay.mode} onChange={(e) => setPay({ ...pay, mode: e.target.value })}>
                  <option value="cash">Cash</option>
                  <option value="upi">PhonePe / GPay</option>
                  <option value="rtgs">RTGS / NEFT / IMPS</option>
                  <option value="loan">Bank Loan (Pending)</option>
                </select>
              </div>
              <div>
                <label className="a1-label">Amount</label>
                <input data-testid="pay-amount" type="number" className="a1-input" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} />
              </div>
              {(pay.mode === "upi" || pay.mode === "rtgs") && (
                <div>
                  <label className="a1-label">UTR / Transaction ID</label>
                  <input data-testid="pay-utr" className="a1-input" value={pay.utr} onChange={(e) => setPay({ ...pay, utr: e.target.value })} />
                </div>
              )}
            </div>
            <button data-testid="pay-submit" onClick={doPay} disabled={busy === "pay"} className="a1-btn a1-btn-action">
              {busy === "pay" ? <Loader2 className="w-5 h-5 animate-spin" /> : "Add Payment"}
            </button>
          </div>
        </div>

        {/* Money summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <SummaryTile label="Selling Price" value={v.selling_price} />
          <SummaryTile label="Total Received" value={t.total_received} color="#16A34A" />
          <SummaryTile label="Loan Pending" value={t.loan_pending} color="#D97706" />
          <SummaryTile label="Customer Pending" value={t.customer_pending} color={t.customer_pending > 0 ? "#EA580C" : "#16A34A"} />
        </div>

        {isOwner && (
          <div className="a1-card p-6 mb-6" style={{ background: "#EFF6FF", borderColor: "#BFDBFE" }} data-testid="profit-panel">
            <div className="a1-label" style={{ color: "#1E3A8A" }}>Owner-only • Profit on this car</div>
            <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 mt-1">
              <div>
                <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Sold Price</span>
                <div className="num font-head font-black text-xl">{formatINR(v.selling_price)}</div>
              </div>
              <div>
                <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Inward Cost</span>
                <div className="num font-head font-black text-xl">{formatINR(v.inward_cost)}</div>
              </div>
              <div>
                <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Profit</span>
                <div className="num font-head font-black text-2xl" style={{ color: (v.profit || 0) >= 0 ? "#16A34A" : "#EA580C" }}>{formatINR(v.profit)}</div>
              </div>
            </div>
          </div>
        )}

        {/* Payment log */}
        {(v.advances?.length > 0 || v.payments?.length > 0) && (
          <div className="a1-card p-6 mb-6">
            <h3 className="font-head font-extrabold text-xl tracking-tight mb-3">Payment History</h3>
            <div className="divide-y divide-slate-200">
              {v.advances?.map((a) => (
                <div key={a.id} className="py-2 flex justify-between text-sm font-semibold">
                  <span>{a.direction === "in" ? "Advance In" : "Refund Out"} • {a.mode} {a.utr && `• ${a.utr}`}</span>
                  <span className="num font-bold" style={{ color: a.direction === "in" ? "#16A34A" : "#EA580C" }}>
                    {a.direction === "in" ? "+" : "-"}{formatINR(a.amount)}
                  </span>
                </div>
              ))}
              {v.payments?.map((p) => (
                <div key={p.id} className="py-2 flex justify-between text-sm font-semibold">
                  <span>{p.mode.toUpperCase()} {p.utr && `• ${p.utr}`}</span>
                  <span className="num font-bold" style={{ color: p.mode === "loan" ? "#D97706" : "#16A34A" }}>{formatINR(p.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Status + notes */}
        <div className="a1-card p-6">
          <h3 className="font-head font-extrabold text-xl tracking-tight mb-4">Status &amp; Bill Notes</h3>
          <div className="flex gap-2 flex-wrap mb-5">
            {["available", "booked", "sold", "delivered"].map((s) => (
              <button
                key={s}
                data-testid={`set-status-${s}`}
                onClick={() => setStatus(s)}
                className={`px-4 py-2.5 rounded-xl font-bold capitalize ${v.status === s ? "text-white" : "bg-slate-100 text-slate-600"}`}
                style={v.status === s ? { background: statusColor[s] } : {}}
              >
                {s === "delivered" && <Truck className="w-4 h-4 inline mr-1" />}
                {s}
              </button>
            ))}
          </div>
          <label className="a1-label">Bill Narration / Terms (prints on invoice)</label>
          <textarea data-testid="bill-notes" className="a1-input !h-auto py-3" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Custom terms for this bill…" />
        </div>
      </div>
    </div>
  );
}

function SummaryTile({ label, value, color }) {
  return (
    <div className="a1-card p-5">
      <div className="a1-label">{label}</div>
      <div className="num font-head font-black text-2xl tracking-tight" style={{ color: color || "#0F172A" }}>{formatINR(value)}</div>
    </div>
  );
}
